/**
 * Scroll reveal
 *
 * Fades-and-rises `.js-animate` elements (and the children of `.js-animate-group`
 * wrappers, staggered) once, on first scroll into view. The `utl-animate` hidden
 * state is only ever applied here, so with JS off - or reduced motion preferred -
 * content simply renders in place, never hidden.
 *
 * Elements already within the initial viewport (the hero title, typically the
 * LCP element) are skipped entirely: hiding them until this footer module runs
 * and then fading back in over 800ms would delay the largest paint. They render
 * in place; only below-the-fold content is hidden and revealed on scroll.
 */

const STAGGER_MS = 90;
const STAGGER_CAP = 6;

export default class Animate {
    constructor() {
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
            return;
        }

        const els = [...document.querySelectorAll('.js-animate')];

        document.querySelectorAll('.js-animate-group').forEach((group) => {
            // Off-canvas panels are emitted as siblings of the cards inside archive
            // groups; they must not be animated - utl-animate's transition would
            // override Foundation's transform transition and kill the drawer slide.
            [...group.children]
                .filter((el) => !el.matches('[data-off-canvas]'))
                .forEach((el, i) => {
                    el.style.setProperty(
                        '--animate-delay',
                        `${Math.min(i, STAGGER_CAP) * STAGGER_MS}ms`
                    );
                    els.push(el);
                });
        });

        if (!els.length) {
            return;
        }

        this.observer = new IntersectionObserver(
            (entries, observer) => {
                entries.forEach((entry) => {
                    if (!entry.isIntersecting) {
                        return;
                    }

                    observer.unobserve(entry.target);
                    entry.target.classList.add('is-animated');
                });
            },
            { rootMargin: '0px 0px -10% 0px' }
        );

        els.forEach((el) => {
            // Above-the-fold on load: leave visible so it never blocks the LCP.
            // The observer's rootMargin trims 10% off the bottom, so match that
            // trigger line rather than the raw viewport height.
            if (el.getBoundingClientRect().top < window.innerHeight * 0.9) {
                return;
            }

            el.classList.add('utl-animate');
            this.observer.observe(el);
        });
    }
}
